import type { ComponentProps, ReactNode } from 'react';
import React from 'react';

import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { slotHeight } from '../Slots/Slots';

const DeviceMetadata = styled('div', { display: 'flex' });
DeviceMetadata.displayName = 'DeviceMetadata';

const deviceSlotsPadding = 16;
const deviceSlotsGap = 8;
const deviceSlotsHeight = slotHeight * 2;
const deviceHeight = deviceSlotsPadding * 2 + deviceSlotsHeight + deviceSlotsGap;

const DeviceDetails = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  gap: '$4',
  maxWidth: '$160',
  minWidth: 0,
  padding: `$${deviceSlotsPadding}`,
});
DeviceDetails.displayName = 'DeviceDetails';

const DeviceSlots = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$32',
  width: 'fit-content',
  minWidth: 'fit-content',
  maxHeight: `$${deviceHeight}`,
  padding: `$${deviceSlotsPadding}`,
  alignItems: 'end',
});
DeviceSlots.displayName = 'DeviceSlots';

const DeviceContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  width: 'fit-content',
  minWidth: 0,
  maxHeight: `$${deviceHeight}`,
  minHeight: 0,
  background: colors.bgApplicationLight,
  borderRadius: '$12',
  boxShadow: shadows.deviceLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.deviceDark,
  },
});
DeviceContainer.displayName = 'DeviceContainer';

export type DeviceProps = {
  slots?: ReactNode | { id: string; content: ReactNode }[];
  metadata?: ReactNode;
} & ComponentProps<typeof DeviceContainer>;

export function Device({ metadata, slots, ...remaining }: DeviceProps) {
  const hasDetails = metadata;

  return (
    <DeviceContainer {...remaining}>
      <DeviceSlots>
        {Array.isArray(slots)
          ? slots.map((slot) => <React.Fragment key={slot.id}>{slot.content}</React.Fragment>)
          : slots}
      </DeviceSlots>
      {hasDetails && (
        <DeviceDetails>{metadata && <DeviceMetadata>{metadata}</DeviceMetadata>}</DeviceDetails>
      )}
    </DeviceContainer>
  );
}
